import type { AudioPlayerLike } from './types.js';

/**
 * Buffer de alignment del karaoke (Fase 36+40). Acumula chars + tiempos
 * absolutos en ms del AudioContext y expone los getters que el RAF loop
 * del consumer usa para destacar/revelar texto sincronizado al audio.
 *
 * Es un objeto plano + mutable que vive detrás de un `useRef` — se lee y
 * escribe sincronicamente desde event handlers del WS y desde RAF, sin
 * disparar re-renders.
 */
export interface AlignmentChunk {
  chars: string[];
  charStartTimesMs: number[];
  charDurationsMs: number[];
  audioStartOffsetMs: number;
}

export interface KaraokeBufferState {
  chars: string[];
  absStartMs: number[];
  absEndMs: number[];
  /** ctx.currentTime (ms) capturado al primer chunk de audio del turno actual. */
  audioStartedAtCtxMs: number | null;
  /**
   * Alignments que llegaron antes del primer chunk binario de audio (orden
   * no garantizado por ElevenLabs sobre WS). Se flushean cuando llega el
   * primer audio y el anchor queda fijado.
   */
  pendingAlignments: AlignmentChunk[];
  /**
   * Fase 40b — timestamp (performance.now()) del primer `tokens` event de
   * este turno. Lo usa el fallback del revelado progresivo para dar
   * velocidad constante mientras todavía no hay alignment.
   */
  tokensStartedAtMs: number | null;
}

export function createKaraokeBufferState(): KaraokeBufferState {
  return {
    chars: [],
    absStartMs: [],
    absEndMs: [],
    audioStartedAtCtxMs: null,
    pendingAlignments: [],
    tokensStartedAtMs: null,
  };
}

/** Aplica un chunk de alignment al buffer usando el anchor actual. */
export function applyAlignmentChunk(buf: KaraokeBufferState, chunk: AlignmentChunk): void {
  const anchor = buf.audioStartedAtCtxMs;
  if (anchor == null) return;
  for (let i = 0; i < chunk.chars.length; i += 1) {
    const startCtxMs = anchor + chunk.audioStartOffsetMs + (chunk.charStartTimesMs[i] ?? 0);
    const endCtxMs = startCtxMs + (chunk.charDurationsMs[i] ?? 0);
    buf.chars.push(chunk.chars[i] ?? '');
    buf.absStartMs.push(startCtxMs);
    buf.absEndMs.push(endCtxMs);
  }
}

/**
 * Marca el primer audio del turno como anchor y flushea cualquier alignment
 * pendiente. Idempotente si el anchor ya estaba seteado.
 */
export function anchorBufferToFirstAudio(
  buf: KaraokeBufferState,
  player: AudioPlayerLike | null,
): void {
  buf.audioStartedAtCtxMs = player?.getClockMs?.() ?? 0;
  if (buf.pendingAlignments.length > 0) {
    for (const chunk of buf.pendingAlignments) applyAlignmentChunk(buf, chunk);
    buf.pendingAlignments = [];
  }
}

/**
 * Agrega un alignment al buffer: si todavía no hay anchor, lo bufferea para
 * el flush; si ya hay anchor, se aplica directo.
 */
export function pushAlignment(buf: KaraokeBufferState, chunk: AlignmentChunk): void {
  if (buf.audioStartedAtCtxMs == null) {
    buf.pendingAlignments.push(chunk);
  } else {
    applyAlignmentChunk(buf, chunk);
  }
}

/**
 * Congela el alignment del turno en curso como tiempos RELATIVOS al inicio de
 * su audio (`startsMs[i]` = ms desde el primer byte de audio del turno hasta
 * que suena el carácter `i`). Se llama al commitear la burbuja, justo antes de
 * {@link clearKaraokeBuffer}, y el snapshot viaja con el `VoiceMessage`: es lo
 * que le permite al REPLAY de la burbuja volver a marcar el karaoke, mapeando
 * `audio.currentTime` contra estos mismos tiempos.
 *
 * `null` cuando no hay nada que congelar (turno sin audio, modo realtime —que
 * no manda alignment— o buffer ya limpio).
 */
export function snapshotKaraokeAlignment(buf: KaraokeBufferState): number[] | null {
  const anchor = buf.audioStartedAtCtxMs;
  if (anchor == null || buf.absStartMs.length === 0) return null;
  return buf.absStartMs.map((abs) => Math.max(0, Math.round(abs - anchor)));
}

/** Limpia todo el buffer — llamado en turn-end / interrupt / stop. */
export function clearKaraokeBuffer(buf: KaraokeBufferState): void {
  buf.chars = [];
  buf.absStartMs = [];
  buf.absEndMs = [];
  buf.audioStartedAtCtxMs = null;
  buf.pendingAlignments = [];
  buf.tokensStartedAtMs = null;
}

const LOOKAHEAD_MS = 250;
const REVEAL_SPEED_MULT = 2.5;
const FALLBACK_CHARS_PER_MS = 0.05; // ~50 chars/seg (~10 palabras/seg).

/**
 * Char activo dentro del `assistantText` actual (Fase 36). -1 si no hay
 * alignment todavía o el bot no está hablando.
 */
export function getKaraokeCharIndex(
  buf: KaraokeBufferState,
  player: AudioPlayerLike | null,
): number {
  if (!player || typeof player.getClockMs !== 'function') return -1;
  if (buf.chars.length === 0) return -1;
  const now = player.getClockMs();
  if (now < buf.absStartMs[0]!) return 0;
  for (let i = buf.chars.length - 1; i >= 0; i -= 1) {
    if (now >= buf.absStartMs[i]!) return i;
  }
  return -1;
}

/**
 * Fase 40 — cantidad de chars a revelar AHORA. Máximo entre:
 *  1) Fallback constante por tiempo desde el primer `tokens` event.
 *  2) Alignment-based: anchor + audioElapsed * SPEED_MULT + LOOKAHEAD_MS.
 * Nunca devuelve `Infinity`.
 */
export function getKaraokeRenderEndIdx(
  buf: KaraokeBufferState,
  player: AudioPlayerLike | null,
): number {
  let revealed = 0;

  const tokensStart = buf.tokensStartedAtMs;
  if (tokensStart != null) {
    const elapsed = performance.now() - tokensStart;
    revealed = Math.max(revealed, Math.floor(elapsed * FALLBACK_CHARS_PER_MS));
  }

  if (
    player &&
    typeof player.getClockMs === 'function' &&
    buf.chars.length > 0 &&
    buf.audioStartedAtCtxMs != null
  ) {
    const audioElapsed = Math.max(0, player.getClockMs() - buf.audioStartedAtCtxMs);
    const target = buf.audioStartedAtCtxMs + audioElapsed * REVEAL_SPEED_MULT + LOOKAHEAD_MS;
    let alignReveal = 0;
    for (let i = buf.absStartMs.length - 1; i >= 0; i -= 1) {
      if (buf.absStartMs[i]! <= target) {
        alignReveal = i + 1;
        break;
      }
    }
    revealed = Math.max(revealed, alignReveal);
  }

  return revealed;
}
